// src/contexts/AuthContext.js - Global authentication state
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  updateProfile 
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { signOut as firebaseSignOut } from '../firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get user data from Firestore
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userInfo = userDoc.data();
            setUser(firebaseUser);
            setUserData(userInfo);
            console.log('User authenticated:', userInfo);
          } else {
            console.warn('User document not found in Firestore');
            setUser(firebaseUser);
            setUserData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(firebaseUser);
          setUserData(null);
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // --- Sign out user ---
  const signOut = async () => {
    try {
      await firebaseSignOut();
      setUser(null);
      setUserData(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  // --- Update user profile ---
  const updateUserProfile = async (updates) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      // Update in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      // Update Firebase Auth display name if provided
      if (updates.displayName) {
        await updateProfile(user, {
          displayName: updates.displayName
        });
      }

      // Reload user data from Firestore
      const updatedDoc = await getDoc(userRef);
      if (updatedDoc.exists()) {
        setUserData({ uid: user.uid, ...updatedDoc.data() });
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  };

  const value = {
    user,
    userData,
    loading,
    signOut,
    updateUserProfile,
    isAuthenticated: !!user,
    isStudent: userData?.role === 'student',
    isCollege: userData?.role === 'college',
    isAdmin: userData?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
